(() => {
  'use strict';
  function initialize(root) {
    if (root.dataset.customLakeInitialized) return;
    root.dataset.customLakeInitialized = 'true';
    const core = globalThis.LBCustomLakeCore;
    const form = root.querySelector('[data-custom-lake-form]');
    const errors = root.querySelector('[data-custom-errors]');
    if (!core || !form || !errors) return;
    let config;
    try { config = JSON.parse(root.querySelector('[data-custom-lake-config]').textContent); }
    catch { errors.hidden = false; errors.textContent = 'Custom ordering is unavailable. Please contact us for help.'; return; }
    // Product pages can link here with an allowed style/size. Ignore other input.
    const parameters = new URLSearchParams(globalThis.location.search);
    if (Object.hasOwn(core.styles, parameters.get('style'))) form.elements.namedItem('style').value = parameters.get('style');
    if (core.sizes.includes(parameters.get('size'))) form.elements.namedItem('size').value = parameters.get('size');
    const add = root.querySelector('[data-custom-add]');
    const review = root.querySelector('[data-custom-review]');
    const summary = root.querySelector('[data-custom-summary]');
    const summaryContent = root.querySelector('[data-custom-summary-content]');
    let submitting = false;
    let uncertain = false;
    let added = false;
    let requestId = null;

    const read = () => Object.fromEntries([...Object.keys(core.fields), 'style', 'size', 'quantity'].map((name) => [name, form.elements.namedItem(name)?.value || '']).concat([['timingAccepted', form.elements.namedItem('timingAccepted')?.checked === true]]));
    const setText = (selector, value) => { const element = root.querySelector(selector); if (element) element.textContent = value; };
    const money = (cents) => Number.isSafeInteger(cents) && cents > 0 ? new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: 'USD' }).format(cents / 100) : 'Price available when ordering opens';

    function clearErrors() {
      errors.hidden = true;
      errors.textContent = '';
      form.querySelectorAll('[aria-invalid]').forEach((field) => field.removeAttribute('aria-invalid'));
      root.querySelectorAll('[data-error-for]').forEach((field) => { field.textContent = ''; });
    }

    function showErrors(fieldErrors, message) {
      clearErrors();
      for (const [name, text] of Object.entries(fieldErrors)) {
        form.elements.namedItem(name)?.setAttribute('aria-invalid', 'true');
        const output = root.querySelector(`[data-error-for="${name}"]`);
        if (output) output.textContent = text;
      }
      errors.hidden = false;
      errors.textContent = message || 'Please check the highlighted details before continuing.';
      errors.focus();
    }

    function refresh() {
      const input = read();
      const selection = core.findSelection(config, input);
      const definition = core.styles[input.style];
      const blockers = core.configurationIssues(config, input);
      setText('[data-custom-price]', money(selection?.variant?.priceCents));
      setText('[data-custom-material]', definition?.material || 'Choose your style');
      setText('[data-custom-timing]', core.timingCopy(config));
      setText('[data-custom-proof]', core.proofCopy(config));
      setText('[data-custom-availability]', added ? 'Added to your cart. Review your lake details there before checkout.' : uncertain ? 'The cart response could not be confirmed. Check your cart before trying again.' : blockers.length ? 'Custom lake ordering is not open yet. You can review your selection below.' : 'Same price as the matching standard blanket. Shipping and tax are calculated at checkout.');
      add.disabled = submitting || uncertain || added || blockers.length > 0;
      add.textContent = submitting ? 'Adding…' : added ? 'Added to cart' : 'Add to cart';
      review.disabled = submitting;
    }

    function reviewSelection() {
      clearErrors();
      const result = core.validateInput(read());
      if (!result.ok) { showErrors(result.errors); return false; }
      const selection = core.findSelection(config, result.values);
      const values = result.values;
      const pairs = [
        ['Lake', values.lakeName],
        ['Location', `${values.nearestTown}, ${values.stateProvince}, ${values.country}`],
        ['Blanket', `${core.styles[values.style].label} · ${core.styles[values.style].material}`],
        ['Size', values.size.replace('x', ' × ') + ' in'],
        ['Quantity', String(values.quantity)],
        ['Price per blanket', money(selection?.variant?.priceCents)],
        ['Design timing', core.timingCopy(config)],
        ['Proof approval', core.proofCopy(config)],
      ];
      if (values.mapReference) pairs.push(['Map reference', values.mapReference]);
      if (values.notes) pairs.push(['Notes', values.notes]);
      summaryContent.replaceChildren();
      for (const [label, value] of pairs) {
        const term = document.createElement('dt');
        const detail = document.createElement('dd');
        term.textContent = label;
        detail.textContent = value;
        summaryContent.append(term, detail);
      }
      summary.hidden = false;
      summary.focus();
      return true;
    }

    function freshRequestId() {
      if (typeof globalThis.crypto?.randomUUID === 'function') return `cl_${globalThis.crypto.randomUUID()}`;
      if (typeof globalThis.crypto?.getRandomValues !== 'function') return null;
      return 'cl_' + Array.from(globalThis.crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');
    }

    async function addToCart() {
      if (submitting || uncertain || added) return;
      if (!reviewSelection()) return;
      requestId ||= freshRequestId();
      const result = core.buildCartItem(config, read(), requestId);
      if (!result.ok) { showErrors(result.errors, 'Custom ordering is not available for this selection yet.'); refresh(); return; }
      // No cart price is accepted from the browser; Shopify charges the variant's price.
      const route = globalThis.Shopify?.routes?.root || '/';
      const cartUrl = new URL(`${route}cart`, globalThis.location.origin);
      const endpoint = new URL(`${route}cart/add.js`, globalThis.location.origin);
      if (endpoint.origin !== globalThis.location.origin) { showErrors({}, 'The cart could not be opened. Please contact us.'); return; }
      submitting = true;
      form.setAttribute('aria-busy', 'true');
      const controls = [...form.elements].filter((field) => !['BUTTON'].includes(field.tagName));
      const priorDisabled = controls.map((field) => field.disabled);
      controls.forEach((field) => { field.disabled = true; });
      refresh();
      try {
        const response = await fetch(endpoint.href, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [result.item] }) });
        const body = await response.json();
        if (!response.ok) {
          // Only an explicit Shopify validation error is a confirmed rejection.
          if ([400, 422].includes(response.status) && body.status === response.status) {
            showErrors({}, 'This blanket could not be added. Please check your selection and try again.');
          } else {
            uncertain = true;
            showErrors({}, 'We could not confirm the add. Check your cart before trying again.');
          }
        } else {
          const line = Array.isArray(body.items) ? body.items.find((item) => String(item.variant_id || item.id) === result.item.id && item.properties?._custom_lake_request_id === requestId) : null;
          if (!line) { uncertain = true; showErrors({}, 'We could not confirm the add. Check your cart before trying again.'); }
          else { added = true; globalThis.location.assign(cartUrl.href); }
        }
      } catch {
        uncertain = true;
        showErrors({}, 'We could not confirm the add. Check your cart before trying again.');
      } finally {
        submitting = false;
        controls.forEach((field, index) => { field.disabled = priorDisabled[index]; });
        form.removeAttribute('aria-busy');
        refresh();
      }
    }

    form.addEventListener('submit', (event) => { event.preventDefault(); event.stopImmediatePropagation(); reviewSelection(); }, true);
    form.addEventListener('input', () => { if (!submitting) { clearErrors(); summary.hidden = true; refresh(); } });
    form.addEventListener('change', () => { if (!submitting) { clearErrors(); summary.hidden = true; refresh(); } });
    review.addEventListener('click', reviewSelection);
    add.addEventListener('click', () => { void addToCart(); });
    refresh();
  }
  function boot(container = document) { container.querySelectorAll('[data-custom-lake-root]').forEach(initialize); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
